/**
 * CRM-UX-01 Stage 04 — Admin-Configurable Labels
 *
 * LabelContext fetches the tenant's label configuration once on mount.
 * Provides useLabel(key, fallback) which returns:
 *   - the tenant override for that key, if one exists
 *   - the platform default for that key, if no override
 *   - the fallback string, if the key is not registered
 *
 * The context never returns a blank string: if the API is unavailable,
 * all useLabel() calls fall back to the provided hardcoded default.
 *
 * DB keys, API contracts, permission identifiers and status codes are
 * never changed — only display_value strings are customizable.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { labelApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const LabelContext = createContext(null);

export function LabelProvider({ children }) {
  const [labelMap, setLabelMap] = useState({});
  const [loading, setLoading]   = useState(false);
  const { user }                = useAuth();

  const refresh = useCallback(async () => {
    if (!user) return;                  // only fetch when authenticated
    setLoading(true);
    try {
      const { data } = await labelApi.list();
      const map = {};
      (data?.labels || []).forEach(item => {
        map[item.label_key] = item.display_value;
      });
      setLabelMap(map);
    } catch {
      // API unavailable — all calls fall back to inline defaults
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <LabelContext.Provider value={{ labelMap, loading, refresh }}>
      {children}
    </LabelContext.Provider>
  );
}

/**
 * useLabel(key, fallback)
 * Returns the resolved display value for the given label key.
 * Falls back to the provided string if the key is not in the map.
 *
 * Example:
 *   const l = useLabel("crm.module.leads", "Leads");
 *   // renders "Enquiries" if admin changed the label, otherwise "Leads"
 */
export function useLabel(key, fallback = "") {
  const ctx = useContext(LabelContext);
  if (!ctx) return fallback;
  return ctx.labelMap[key] || fallback;
}

/** Direct access to the full label map and refresh function. */
export function useLabelContext() {
  const ctx = useContext(LabelContext);
  if (!ctx) throw new Error("useLabelContext must be used inside LabelProvider");
  return ctx;
}

export default LabelContext;
